import { Location } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { CertificationService } from "certification/services/certification-service";
import { DestinationPageType } from "components/eprescribe/eprescribe.component";
import { FaxModalComponent } from "components/fax/fax-modal/fax-modal";
import {
  Checkbox,
  Events,
  IonicPage,
  LoadingController,
  ModalController,
  NavParams,
  PopoverController,
  ToastController,
  Toggle,
} from "ionic-angular";
import {
  getHistoryDetailSummary,
  HistoryDetailSummary,
} from "models/order-management/historyDetailSummary";
import {
  Action,
  DiscontinuedAction,
  DropdownItem,
  OpenSearchResponse,
  Order,
  OrderErrorResponse,
  OrderPatchRequest,
  OrderResponse,
  OrderSearchDateRange,
  OrderSearchRequest,
  OrderSignatureRequest,
  OrderSignatureRequestSignature,
  OrderTypeConfiguration,
} from "models/order-management/models";
import { StatefulOrderResponse } from "models/order-management/statefullOrderResponse";
import {
  NciAddendum,
  NciAddendumGenerationResult,
} from "models/patient-chart/nci-addendum";
import { NciIndicator } from "models/patient-chart/nci-indicator";
import { isProfessionalProvider } from "models/resource-management/company-resource-type-guards";
import CredentialedResource from "models/resource-management/credentialed-resource";
import { PatientResponseDto } from "models/scriptSure";
import * as moment from "moment-timezone";
import { EditViewPopoverPage } from "navigation/components/edit-view-popover/edit-view-popover";
import {
  AddOrder,
  PrefilledData,
} from "pages/orders-entry-om/add-order/add-order";
import { LastFirstPatientName } from "pipes/output-formatters/last-first-patient-name";
import { UtcDateTimeZonePipe } from "pipes/output-formatters/utc-date-timezone.pipe";
import { CareTeamsService } from "providers/care-teams-service/care-teams-service";
import { DocManagementDataService } from "providers/doc-management-data-service/doc-management-data-service";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { NciAddendumsService } from "providers/nci-addendums/nci-addendums-service";
import { NciIndicatorsService } from "providers/nci-indicators/nci-indicators-service";
import { OrderManagementDataService } from "providers/order-management-data-service/order-management-data-service";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { ReportExecutionService } from "providers/report-execution-service/report-execution.service";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { Observable, Subscription } from "rxjs";
import { forkJoin } from "rxjs/observable/forkJoin";
import { of } from "rxjs/observable/of";
import { catchError, filter, mergeMap, switchMap } from "rxjs/operators";
import Utils from "utils/Utilities";
import { AuthService } from "../../../app/auth/auth.service";
import PatientChart from "../../../models/patient-chart/patientChartResponse";
import { NavigationService } from "../../../navigation/navigation.service";
import { OrderDataService } from "../../../providers/order-data-service/order-data-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { BasePage } from "../../base-page";
import { OrderFormState } from "../add-order/order-form-state";
import {
  getDiscontinuedProviderDisplayName,
  getOrderTypeDisplayName,
  OrderTypeSubtype,
} from "../order-utils";

/**
 * Generated class for the OrderEntryPage page.
 *
 * See https://ionicframework.com/docs/components/#navigation for more info on
 * Ionic pages and navigation.
 */

const DATE_RANGE_COLUMN = "startDate";
const NO_ORDERS_TO_PRINT =
  "There are no orders to print based on the selected filters";
const NO_ORDERS_TO_FAX =
  "There are no orders to fax based on the selected filters";

export interface AddOrderModalParams {
  orderTypeConfigurations: Array<OrderTypeConfiguration>;
  patientId: number;
  patientChartId: number;
  orders: Array<StatefulOrderResponse>;
  formType: string;
  credentialedResource: CredentialedResource;
  locOrders: Array<StatefulOrderResponse>;
  vfOrders: Array<StatefulOrderResponse>;
  isEPrescribeEnabled: boolean;
  prefilledData: PrefilledData;
}

export interface AddOrderModalOpts {
  enableBackdropDismiss: boolean;
  cssClass: string;
}

@IonicPage({
  segment: "patient/:patChartId/orderEntryOM",
})
@Component({
  selector: "page-order-om-entry",
  templateUrl: "order-entry-om.html",
})
export class OrderEntryOMPage extends BasePage implements OnInit, OnDestroy {
  patientChart: PatientChart;
  numOpenOrders = 0;
  segment = "newOrders"; //sets initial segment to 'New Orders'
  orderTypeOptions = [];
  categoryOptions = [];
  roleEnums = [];
  showOrderManagement = true;
  patientId: any;
  patientCareType: string = "";
  patientCareTypeSubscription: Subscription;
  searchForm: FormGroup;
  error: boolean = false;
  searchField = new FormControl("");
  credentialedResource: CredentialedResource;
  userDisplayName: string;
  type: string = "OE";
  benefitPeriodCertificationEndDate: Date;
  pdfSrc: string;

  // Filters
  orders: Array<StatefulOrderResponse> = [];
  locOrders: Array<StatefulOrderResponse> = null;
  vfOrders: Array<StatefulOrderResponse> = null;

  // TRD-201 / REQ-006 (TRD §4.4): "N" indicator data sourced from
  // GET /nci-indicators?orderId=. Keyed by `order.id` for O(1) template lookup
  // via `nciIndicators?.get(order.id)`. Refreshed alongside `this.orders` in
  // loadData(). Re-initialized to empty Map (not null) on each load so the
  // template's `?.get(...)` never throws and an in-flight fetch doesn't leave
  // stale rows showing the icon for a different order set.
  nciIndicators: Map<number, NciIndicator> = new Map<number, NciIndicator>();
  orderTypeConfigurations: Array<OrderTypeConfiguration> = [];
  fromDate: any = null;
  toDate: any = null;
  orderSearchDetails: OrderSearchRequest = null;
  search: string = null;
  discontinuedOrdersEnabled: boolean = true;

  selectAllOrdersCheckbox = false;
  checkedOrdersMap: Record<string, boolean> = {};
  checkedOrders: string[] = [];
  totalPageCount: number = 1;
  currentPage: number = 0;
  pageSize: number = 100;
  sortColumn: string = "startDate";
  sortDirection: "asc" | "desc" = "desc";
  orderTemp = null;
  historyDetails: HistoryDetailSummary[] = [];

  orderData = [];
  orderTypeDropdownItems: Array<DropdownItem> = [];
  orderTypes: string;
  spinnerCounter = 0;
  spinner = null;

  teamName: string = "";
  teamId: number = 0;
  teamQbosId: number = 0;
  selectPrintOptions: any[];
  printOptions: any[] = [{ displayName: "Print", key: "print" }];

  faxOptions: any[] = [{ displayName: "Fax", key: "fax" }];

  prefilledData: PrefilledData = null;
  signedOrdersEnabled = true;
  signedStatus?: OrderSignatureRequest.SignedStatusEnum = null;
  hospiceCoverage: boolean = null;

  showNoOrdersMsg = false;
  patientChartIdProviderPanel: number = 0;
  externalIdProviderPanel: number = 0;

  // Subscription functions
  featureToggleLoadedFn: Function;
  reloadNavBarFn: Function;
  westFaxToggleSubscription: Subscription;
  worldViewFaxToggleSubscription: Subscription;
  nciAddendumnsSubscription: Subscription;
  tempNciToggleSubscription: Subscription;
  tempNciToggle: boolean = false;

  orderTypesWithHospiceCoverage = [];
  showEPrescribeIframe = false;
  destinationPage: DestinationPageType = "drug-list";

  addOrderModalParams: AddOrderModalParams;
  addOrderModalOpts: AddOrderModalOpts;

  isOrderRejected: boolean = false;
  isOrderDiscontinued: boolean = false;

  private scriptSurePatient: PatientResponseDto | null;
  private patientSubscription: Subscription;

  constructor(
    public modalCtrl: ModalController,
    public navSvc: NavigationService,
    public navParams: NavParams,
    public stateData: StateManagementService,
    public dataService: OrderDataService,
    public events: Events,
    private fb: FormBuilder,
    private orderManagementDataService: OrderManagementDataService,
    private toastCtrl: ToastController,
    private referralsService: ReferralsServiceProvider,
    private navBarService: NavBarServiceProvider,
    protected translate: TranslateService,
    private resourceManagementService: ResourceManagementService,
    private popoverCtrl: PopoverController,
    private documentSvc: DocManagementDataService,
    public loadingCtrl: LoadingController,
    private careTeamService: CareTeamsService,
    private elementRef: ElementRef,
    private reportExecutionService: ReportExecutionService,
    private location: Location,
    private nciAddendumsService: NciAddendumsService,
    private nciIndicatorsService: NciIndicatorsService,
    private utcDateTimeZonePipe: UtcDateTimeZonePipe,
    private authSvc: AuthService,
    private certificationService: CertificationService
  ) {
    super(stateData);
    this.prefilledData = null;
    this.patientCareType = this.stateData.patientCareType.toString();
    this.searchForm = this.fb.group({
      fromDate: this.fb.control(""),
      toDate: this.fb.control(""),
      type: this.fb.control(null),
      hospiceCoverage: this.fb.control(null),
    });
    // this.checkAccessLevel();
    this.referralsService.ACTIVE_TAB_NAME = "order-entry-om";
    this.patientChart = this.navBarService.getPatientChart();

    // Subscription functions
    this.featureToggleLoadedFn = () => {
      this.navBarService.initializePatientData({
        id: this.navParams.data.patChartId,
        urlHash: `${this.navParams.data.patChartId}`,
      });
      // Update print/fax options after feature toggles are loaded
      this.makeSelectPrintOptions();
    };

    this.reloadNavBarFn = () => {
      this.patientChart = this.navBarService.getPatientChart();
      if (this.patientChart) {
        this.loadData();
        this.loadLOCOrders();
        this.loadVFOrders();
        this.loadCurrentbenefitPeriodCertificationEndDate();
        // Update print/fax options when navbar is reloaded
        this.makeSelectPrintOptions();

        // CO-1302: iFrame flag-setting logic moved to scriptSurePatient subscription
        // (see patientSubscription below) to ensure it works regardless of whether
        // initializePatientData() is called

        this.navBarService.events.unsubscribe("reloadNavBar");
      }
    };
    this.prefilledData = this.navParams.data.prefilledData;
  }

  @ViewChild("orderTypeDropdown") orderTypeDropdown;
  @ViewChild("hospiceCoverageDropdown") hospiceCoverageDropdown;

  refresh() {
    this.loadData();
    this.loadLOCOrders();
    this.loadVFOrders();
  }

  generateNonCoveredItemsAddendum() {
    // TDR-15 (F1): clinical-ui supplies the non-covered order IDs from its already-loaded
    // order grid. Server (patient-charts) uses this list for the empty-check (422 +
    // NCI_NO_NON_COVERED_ITEMS) and for the post-generation junction-row writes.
    //
    // Predicate: `hospicePays === false` — strict equality, NOT `!o.hospicePays`. Orders
    // that don't carry the field at all (e.g. LEVEL_OF_CARE) must be excluded; only
    // orders whose order-management coverage flag is explicitly false are non-covered.
    // (`nciReportedAction` was the wrong gate — it marks orders that have ALREADY been
    // included in a prior addendum via OMF's PatchNciReported flow, so it can never
    // identify newly non-covered orders. The page's "N" indicator render is now driven
    // by the separate `nciIndicators` map, not by either field.)
    //
    // Id source: `orderId` is the OpenAPI-declared field on Order/OrderResponse; the
    // search API returns it as a string, so coerce to number for the JSON request body.
    const nonCoveredOrders = this.orders.filter(
      (o) => o.hospicePays === false
    );
    const nonCoveredOrderIds: number[] = nonCoveredOrders.map((o) =>
      Number(o.orderId)
    );

    // CO-3652 (REQ-039 §4.16): the server gate (NonCoveredItemsAddendumServiceImpl,
    // NCI_MISSING_ITEM_REASON) refuses generation unless every non-covered order
    // resolves to a non-blank plain-language reason. The reason is each order's
    // resolved `coverageDesignation` label (om-details internally names this the
    // "reason for non-coverage"). Pre-check locally and block with a naming toast
    // rather than POSTing and letting the server 422.
    const ordersMissingReason = nonCoveredOrders.filter(
      (o) => !this.resolveCoverageDesignationLabel(o)
    );
    if (ordersMissingReason.length > 0) {
      const ids = ordersMissingReason.map((o) => o.orderId).join(", ");
      const noun = ordersMissingReason.length === 1 ? "order" : "orders";
      this.toastCtrl
        .create({
          message:
            `Cannot generate: ${noun} ${ids} ` +
            (ordersMissingReason.length === 1 ? "is" : "are") +
            " missing a coverage-designation reason. Set it on the " +
            `${noun}, then try again.`,
          duration: 6000,
          position: "below",
          cssClass: "error",
        })
        .present();
      return;
    }

    // Build the per-item reason map keyed by numeric orderId → resolved label.
    const itemReasons: { [orderId: number]: string } = {};
    nonCoveredOrders.forEach((o) => {
      itemReasons[Number(o.orderId)] =
        this.resolveCoverageDesignationLabel(o);
    });

    const nciAddendumRequest: Partial<NciAddendum> = {
      patientChartId: this.patientChart.id,
      nonCoveredOrderIds,
      itemReasons,
    };

    this.increaseSpinnerCount("Generating NCI Addendums ...");
    this.nciAddendumnsSubscription = this.nciAddendumsService
      .createAndGenerateNciAddendums(
        this.patientChart.id,
        nciAddendumRequest as NciAddendum
      )
      .finally(() => this.decreaseSpinnerCount())
      .subscribe(
        (response: NciAddendumGenerationResult) => {
          // TDR-16 / wave 15.5a — POST now returns JSON envelope
          // {addendumId, pdf}. `response.pdf` is the same base64 string the
          // endpoint historically returned as a bare String body, so the
          // download path is unchanged below.
          const blob = this.b64toBlob(response.pdf, "application/pdf");
          this.pdfSrc = URL.createObjectURL(blob);
          window.open(this.pdfSrc, "_blank");

          // TDR-16 / §7.4 — F1 cross-service path: chain PATCH /orders/{orderId} to
          // order-management-functions per non-covered orderId so OMF appends one
          // NON_COVERED_ITEM_ADDENDUM_GENERATED Order History entry per affected order.
          // Receive side delivered wave 14 (`PatchNciGenerated` in OMF action-dispatcher).
          // `response.addendumId` (new in wave 15.5a) flows through as `nciAddendumId`
          // on each PATCH so REQ-006's Order History "N" indicator can deep-link back
          // to the addendum.
          this.patchOmfForGeneratedAddendum(
            nonCoveredOrderIds,
            response.addendumId
          );
        },
        (error) => {
          console.error("Error creating NCI Addendum:", error);
          // CO-3640 §2.1 — when generation is blocked the endpoint returns
          // HTTP 422 + {code, message}. NCI_DETERMINATION_INCOMPLETE names the
          // mandatory-election requirement still "Awaiting Determination" as the
          // blocker; the sibling blocked codes (NCI_NO_NON_COVERED_ITEMS /
          // NCI_MISSING_ITEM_REASON) use the same envelope. Surface the
          // server-provided message verbatim so the clinician sees exactly which
          // determination is blocking, rather than the generic
          // presentErrorToast "Communication error." text. This is the FE
          // backstop: the Generate button here gates only on the presence of
          // non-covered orders (it has no addendum-status context), so the 422
          // is the authoritative signal that generation is gated.
          const blockedMessage = this.nciBlockedGenerationMessage(error);
          if (blockedMessage) {
            this.toastCtrl
              .create({
                message: blockedMessage,
                duration: 6000,
                position: "below",
                cssClass: "error",
              })
              .present();
          } else {
            this.presentErrorToast(error, "Error creating NCI Addendums");
          }
        }
      );
  }

  /**
   * CO-3652 (REQ-039 §4.16) — Resolve an order's `coverageDesignation` code to
   * its plain-language dropdown label, walking `this.orderTypeConfigurations`.
   *
   * This intentionally replicates om-details' `transformCoverageDesignationCode`
   * resolver locally (kept in scope rather than refactoring the shared
   * component). Unlike that resolver — which returns the raw `value` as a
   * display fallback when resolution fails — this one returns "" on ANY miss
   * (no orderType config, no coverageDesignation component, no matching
   * dropdown item, no label, or no code on the order at all). The empty string
   * is the caller's "no reason" signal that drives the pre-check + block toast,
   * so we must NOT fall back to the raw code here.
   */
  private resolveCoverageDesignationLabel(
    order: StatefulOrderResponse
  ): string {
    if (!order || !order.coverageDesignation) {
      return "";
    }

    const componentOrderType = this.orderTypeConfigurations.find(
      (config) => config.orderType === order.orderType
    );
    const orderTypeComponents = componentOrderType
      ? componentOrderType.components
      : undefined;
    if (!orderTypeComponents) {
      return "";
    }

    const componentReasonForNonCoverage = orderTypeComponents.find(
      (component) =>
        component.properties &&
        component.properties.some(
          (property) => property.name && property.name === "coverageDesignation"
        )
    );
    if (
      !componentReasonForNonCoverage ||
      !componentReasonForNonCoverage.properties[0].dropdownItems
    ) {
      return "";
    }

    const dropdownItemsCoverageDesignation =
      componentReasonForNonCoverage.properties[0].dropdownItems;
    const coverageDesignation = dropdownItemsCoverageDesignation.find(
      (item) => item.value === order.coverageDesignation
    );
    if (!coverageDesignation || !coverageDesignation.label) {
      return "";
    }

    return coverageDesignation.label;
  }

  /**
   * CO-3640 §2.1 — Extract the human-readable blocker message from a blocked
   * NCI-generation 422 envelope ({code, message}). Returns the server `message`
   * for the recognized blocked codes (falling back to a generic gate message if
   * the body omits it), or null when the error is not a recognized NCI
   * blocked-generation 422 — in which case the caller uses the generic error
   * toast. Kept defensive: never throws on a missing / malformed envelope.
   */
  private nciBlockedGenerationMessage(err: HttpErrorResponse): string | null {
    const NCI_BLOCKED_GENERATION_CODES = [
      "NCI_DETERMINATION_INCOMPLETE",
      "NCI_NO_NON_COVERED_ITEMS",
      "NCI_MISSING_ITEM_REASON",
    ];
    const envelope = err && err.error ? err.error : null;
    const code =
      envelope && typeof envelope.code === "string" ? envelope.code : null;
    if (
      err &&
      err.status === 422 &&
      code &&
      NCI_BLOCKED_GENERATION_CODES.indexOf(code) >= 0
    ) {
      const message =
        envelope && typeof envelope.message === "string"
          ? envelope.message
          : null;
      return message || "Cannot generate the Non-Covered Items Addendum yet.";
    }
    return null;
  }

  /**
   * TDR-16 / §7.4 — Fan out one PATCH /orders/{orderId} per non-covered orderId so OMF
   * writes a NON_COVERED_ITEM_ADDENDUM_GENERATED Order History entry per affected order.
   *
   * Failure semantics: PATCH errors are non-blocking. The addendum generation already
   * succeeded (PDF rendered, addendum row + junction rows + DM upload + SNS event all
   * happened server-side); a failure here only means Order History is incomplete. We
   * surface a non-blocking warning toast and let the user know they can retry by
   * refreshing. We do NOT undo the generation.
   */
  private patchOmfForGeneratedAddendum(
    nonCoveredOrderIds: number[],
    nciAddendumId: number
  ): void {
    if (!nonCoveredOrderIds || nonCoveredOrderIds.length === 0) {
      return;
    }
    const datetime = new Date().toISOString();
    const timezone = moment.tz.guess();
    const patches = nonCoveredOrderIds.map((orderId) =>
      this.orderManagementDataService
        .updateOrder(String(orderId), {
          actionType: Action.ActionTypeEnum.NonCoveredItemAddendumGenerated,
          // wave 15.5a — patient-charts POST now surfaces the new addendum id in
          // the JSON envelope; carried here as `nciAddendumId` so OMF writes the
          // back-reference on each Order History row (REQ-006 deep link).
          nciAddendumId,
          displayName: this.userDisplayName,
          datetime,
          timezone,
        })
        .catch((err) => {
          console.warn(
            `OMF PATCH failed for orderId=${orderId} (NCI Generated):`,
            err
          );
          return of(null);
        })
    );
    forkJoin(patches).subscribe((results) => {
      const failed = results.filter((r) => r === null).length;
      if (failed > 0) {
        this.toastCtrl
          .create({
            message: `Order History may be incomplete for ${failed} order(s) — refresh the page to retry.`,
            duration: 6000,
            position: "below",
            cssClass: "error",
          })
          .present();
      }
    });
  }

  getDiscontinueDateDisplay(order: StatefulOrderResponse) {
    let timezone = order.discontinuedAction
      ? order.discontinuedAction.timezone
      : undefined;
    return order.canceledAction
      ? "Canceled"
      : this.utcDateTimeZonePipe.transform(
          order.discontinueDate,
          timezone,
          "dateOnly"
        );
  }

  exitOrderEntry() {
    this.location.back();
  }

  returnToOrderEntry() {
    this.navSvc.goToInternalPage("OrderEntryOMPage", {
      patChartId: this.navBarService.getExternalId().toString(),
      assessmentId: this.stateData.getAssessmentId() || null,
      assessmentUuid: this.stateData.getAssessmentUuid() || null,
    });
  }

  ngAfterViewInit() {
    // CO-1302: Searchbar is not rendered when showEPrescribeIframe is true
    const searchIcon = this.elementRef.nativeElement.querySelector(
      ".searchbar-search-icon"
    );
    if (searchIcon) {
      searchIcon.addEventListener(
        "click",
        this.getItemsBySearchFilter.bind(this)
      );
    }
  }

  ionViewWillEnter(): void {
    this.makeSelectPrintOptions();
  }

  showConvertToOrderForm() {
    // CO-1302: Don't open modal if navigating to eRx iFrame
    if (this.prefilledData && !this.showEPrescribeIframe) {
      this.presentAddOrderModal(true);
    }
  }

  getOrderTypeConfigurations(): void {
    this.orderManagementDataService
      .getOrderTypes()
      .subscribe((orderTypeConfigurations) => {
        this.orderTypeConfigurations = orderTypeConfigurations;
        this.setOrderTypeList(this.orderTypeConfigurations);

        this.orderTypesWithHospiceCoverage = this.orderTypeConfigurations
          .filter(
            (configuration) =>
              configuration.components &&
              configuration.components.some(
                (component) =>
                  component.properties &&
                  component.properties.some(
                    (property) => property.name === "hospicePays"
                  )
              )
          )
          .map((configuration) => configuration.orderType);
        this.showConvertToOrderForm();
      }, OrderErrorResponse.handleError(this.toastCtrl, "Could not get order type configurations."));
  }

  loadData(): void {
    this.increaseSpinnerCount("Loading orders ...");
    this.orders = [];
    // TRD-201 / REQ-006: clear stale indicators before refetch — same lifecycle as `this.orders`.
    this.nciIndicators = new Map<number, NciIndicator>();
    // CO-3363: selections are keyed by orderId against the rows that were on
    // screen. Filtering, searching, sorting or paging replaces those rows, so a
    // surviving selection meant Print/Fax acted on orders the user could no longer
    // see — and the audit trail recorded them as Printed. Clear with the rows.
    this.selectAllOrdersCheckbox = false;
    this.checkedOrdersMap = {};
    this.checkedOrders = [];
    this.createSearchRequestBody();

    if (
      !this.signedOrdersEnabled &&
      !(
        !this.signedStatus ||
        this.signedStatus == OrderSignatureRequest.SignedStatusEnum.No
      )
    ) {
      // CO-3363: this early return skipped the matching decreaseSpinnerCount(),
      // so the loading overlay raised two lines above was never dismissed and the
      // page was left permanently blocked behind it.
      this.decreaseSpinnerCount();
      return;
    }
    let details: OrderSearchRequest = {};
    details.patientChartId =
      this.externalIdProviderPanel > 0
        ? this.externalIdProviderPanel
        : this.patientChart.id;
    details.from = this.currentPage * this.pageSize;
    details.size = this.pageSize;
    details.includeDiscontinued = true;

    this.orderManagementDataService
      .searchOrders(this.orderSearchDetails)
      .finally(() => this.decreaseSpinnerCount())
      .pipe(
        switchMap((searchResults) => {
          if (!searchResults.hits) {
            return this.orderManagementDataService.searchOrders(details);
          } else {
            if (searchResults && searchResults.hits) {
              this.showNoOrdersMsg = false;
              this.orders = searchResults.hits;
              this.orders = this.orders.map((s) => {
                s = {
                  ...s,
                  showMorePNotes: false,
                  showMoreDReason: false,
                  showMoreRnNotes: false,
                  showMoreHReason: false,
                  showMoreRGIP: false,
                  showMorePRNR: false,
                  isOrderRejected: this.setIsOrderRejected(s),
                  isOrderDiscontinued: this.setIsOrderDiscontinued(s),
                };
                if (s.isOrderRejected) {
                  s.reasonForOrderRejection =
                    this.setRejectedReasonIfOrderRejectedAndNoReasonProvided(s);
                }
                this.setDiscontinuedActionReasonIfOrderDiscontinuedAndNoReasonProvided(
                  s
                );
                return s;
              });
              this.totalPageCount = Math.ceil(
                searchResults.totalHits / this.pageSize
              );
            } else {
              this.orders = [];
            }
          }
          const emptyObject: OpenSearchResponse = {};
          return of(emptyObject);
        })
      )
      .subscribe((searchResults) => {
        this.showNoOrdersMsg = searchResults.totalHits == 0;
        // TRD-201 / REQ-006 (TRD §4.4): after orders settle, fetch indicators for the
        // current grid page. Best-effort UX — see NciIndicatorsService for fail-open
        // semantics. Runs after the orders are populated so the icon binds via
        // `nciIndicators?.get(order.id)` in the template.
        this.loadNciIndicators();
      }, OrderErrorResponse.handleError(this.toastCtrl, "Error loading orders."));
  }

  /**
   * TRD-201 / REQ-006: Fetch NCI indicators for the currently-loaded orders and rebuild
   * `this.nciIndicators` (Map keyed by orderId) for template lookup.
   *
   * OMF order responses carry `orderId`, not `id`. Coerce via Number(o.orderId) to match the
   * wave-11 POST predicate (see generateNonCoveredItemsAddendum) and the Map<number, NciIndicator>
   * key type. The earlier `(o as any).id` cast always yielded undefined, so `orderIds` stayed
   * empty and NciIndicatorsService.getIndicators short-circuited to of([]) — the HTTP call
   * never fired and no "N" icon ever rendered.
   *
   * Only currently-non-covered orders (`hospicePays === false`) contribute to the request:
   * the backend NCI Indicators endpoint scopes its query to `non_covered_item_order_link`
   * junction rows, which are written only at addendum-generation time and only for
   * non-covered orders. Mirrors the wave-11 `generateNonCoveredItemsAddendum` POST
   * predicate for the `nonCoveredOrderIds` array (see commit aacacef101).
   */
  private loadNciIndicators(): void {
    if (!this.tempNciToggle) {
      return;
    }
    const orderIds: number[] = (this.orders || [])
      .filter((o) => o.hospicePays === false)
      .map((o) => Number(o.orderId))
      .filter((id) => !Number.isNaN(id));
    this.nciIndicatorsService.getIndicators(orderIds).subscribe((indicators) => {
      const map = new Map<number, NciIndicator>();
      for (const indicator of indicators || []) {
        map.set(indicator.orderId, indicator);
      }
      this.nciIndicators = map;
    });
  }

  /**
   * Template helper for the "N" non-covered-item indicator on the orders grid.
   *
   * Returns the NciIndicator for an order, or undefined if none. OMF order responses
   * carry `orderId`, not `id`, so the lookup must use `order.orderId`; coerce to a
   * number to match the Map<number, NciIndicator> key set by `loadNciIndicators`
   * (`map.set(indicator.orderId, ...)` — backend returns orderId as a number).
   *
   * Exists as a component method because Angular 5 / Ionic 3 template expressions
   * do not expose `Number` as a global, so `Number(order.orderId)` cannot be invoked
   * inline in the template. Centralizing the coercion here also keeps the lookup
   * semantics identical between the `*ngIf` predicate and the date-binding read.
   */
  getNciIndicator(order: any): NciIndicator | undefined {
    if (!this.nciIndicators || !order) {
      return undefined;
    }
    const key = Number(order.orderId);
    if (Number.isNaN(key)) {
      return undefined;
    }
    return this.nciIndicators.get(key);
  }

  setDiscontinuedActionReasonIfOrderDiscontinuedAndNoReasonProvided(
    order: StatefulOrderResponse
  ) {
    if (order.discontinuedAction) {
      if (!order.discontinuedAction.reason) {
        order.discontinuedAction.reason = "No Details Entered (via User)";
      }
    }
  }

  setRejectedReasonIfOrderRejectedAndNoReasonProvided(
    order: StatefulOrderResponse
  ): string {
    const NO_REASON_MESSAGE = "No Details Entered (via User)";
    let reason: string;
    if (order.signatureRequests?.created?.signature) {
      const signature = order.signatureRequests.created.signature;
      if (signature.actionType === Action.ActionTypeEnum.Rejected) {
        if (Action.hasReason(signature)) {
          reason = signature.reason;
        } else {
          reason = NO_REASON_MESSAGE;
        }
      }
    }
    return reason;
  }

  getUserInformation() {
    // CO-3363: `AuthService.getUser()` is an in-memory read that is never
    // rehydrated, so it is null on a cold deep link to
    // `#/patient/:patChartId/orderEntryOM`. Dereferencing `.userId` threw, which
    // meant getOrderTypeConfigurations() never ran — leaving orderTypeConfigurations
    // empty, `canAddOrder` false forever and every order type unresolvable, with no
    // error shown.
    const user = this.authSvc.getUser();
    if (!user) {
      this.toastError(
        "Unable to determine the signed-in user. Please reload the page."
      );
      return;
    }
    this.resourceManagementService
      .getCredentialedResourceByExternalId(user.userId)
      .subscribe((provider) => {
        this.credentialedResource = provider;
        this.userDisplayName = provider.findDisplayName(new Date());
        this.getOrderTypeConfigurations();
      }, OrderErrorResponse.handleError(this.toastCtrl, "Failed to retrieve user information."));
  }

  ngOnInit(): void {
    this.patientChartIdProviderPanel = this.navParams.data.patChartId;
    this.externalIdProviderPanel = Number(
      this.navParams.data.externalPatientChartId
    );
    if (
      this.patientChart &&
      this.patientChart.id === this.navParams.data.patChartId
    ) {
      this.loadData();
      this.loadLOCOrders();
      this.loadVFOrders();
    } else {
      // on cold load of page, load patient chart data after features are retrieved
      if (!this.navBarService.featureToggleData) {
        this.navBarService.initFeatureToggleData();
      } else {
        // Can occur if switching chart ids or navigating to this page from somewhere other than patient chart
        this.navBarService.initializePatientData({
          id: this.navParams.data.patChartId,
          urlHash: `${this.navParams.data.patChartId}`, // window.location is updated after this function runs, so must pass this in
        });
      }
      this.navBarService.events.subscribe(
        "featureToggleLoaded",
        this.featureToggleLoadedFn
      );
    }
    this.navBarService.events.subscribe("reloadNavBar", this.reloadNavBarFn);
    this.reloadNavBarFn(); //added this as above line was not calling the function.
    this.stateData.setAssessmentType("orderManagement");
    this.stateData.setSectionId("orderEntry");
    this.stateData.setAssessmentId(null);
    this.patientCareTypeSubscription = this.stateData
      .getPatientCareTypeObserver()
      .subscribe((careType: string) => {
        this.patientCareType = careType;
      });
    // CO-3363: `getExternalId()` is undefined until the nav bar has resolved the
    // patient, which it has NOT on a cold deep link to
    // `#/patient/:patChartId/orderEntryOM`. Calling `.toString()` on it threw
    // here and aborted the REST of ngOnInit — so getUserInformation() below never
    // ran (leaving no order types, an unusable Add Order button and every order
    // type unresolvable), and neither did the fax-toggle, NCI-toggle and
    // ScriptSure subscriptions that follow. The page looked loaded because
    // loadData() had already run further up.
    const careTeamExternalId = this.navBarService.getExternalId();
    if (careTeamExternalId) {
      this.careTeamService
        .getCareTeamMembers(careTeamExternalId.toString())
        .subscribe((data) => {
          const team = data && data["team"];
          if (team) {
            this.teamName = team.name;
            this.teamId = team.id;
            this.teamQbosId = team.qbosId;
          }
        }, () => {
          // Non-blocking: an order created without team attribution is better
          // than a page that never finishes initialising.
        });
    }
    this.getUserInformation();

    // Subscribe to WestFax and WorldViewFax toggle changes to update the menu options
    this.westFaxToggleSubscription = this.stateData.isWestFaxToggleOn.subscribe(
      () => {
        this.makeSelectPrintOptions();
      }
    );
    this.worldViewFaxToggleSubscription =
      this.stateData.isWorldViewFaxToggleOn.subscribe(() => {
        this.makeSelectPrintOptions();
      });
    this.tempNciToggleSubscription = this.stateData.tempNciToggle.subscribe(
      (value) => {
        this.tempNciToggle = value;
      }
    );
    this.patientSubscription = this.navBarService
      .getScriptSurePatient()
      .subscribe((scriptSurePatient) => {
        this.scriptSurePatient = scriptSurePatient;

        // CO-1302: Handle navigation from "Convert to eRx" button in medications popover
        // Set iFrame flags when scriptSurePatient data becomes available
        // This works regardless of whether initializePatientData() was called
        if (
          this.navParams.data.showEprescribeIframe === true &&
          scriptSurePatient &&
          !this.showEPrescribeIframe
        ) {
          this.showEPrescribeIframe = true;
          this.destinationPage = "drug-list";
        }
      });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.patientCareTypeSubscription) {
      this.patientCareTypeSubscription.unsubscribe();
    }
    if (this.westFaxToggleSubscription) {
      this.westFaxToggleSubscription.unsubscribe();
    }
    if (this.worldViewFaxToggleSubscription) {
      this.worldViewFaxToggleSubscription.unsubscribe();
    }
    if (this.tempNciToggleSubscription) {
      this.tempNciToggleSubscription.unsubscribe();
    }
    if (this.nciAddendumnsSubscription) {
      this.nciAddendumnsSubscription.unsubscribe();
    }
    this.navBarService.events.unsubscribe("reloadNavBar", this.reloadNavBarFn);
    this.navBarService.events.unsubscribe(
      "featureToggleLoaded",
      this.featureToggleLoadedFn
    );
    if (this.patientSubscription) {
      this.patientSubscription.unsubscribe();
    }
    URL.revokeObjectURL(this.pdfSrc);
  }

  ionViewCanEnter() {
    return this.authSvc.authenticated();
  }

  sendToPhysicianPanel() {
    let newNavParams = {};
    this.navSvc.goToInternalPage("PhysicianPanelPage", newNavParams);
  }

  onArrowClicked(order: OrderResponse): void {
    const orderTypeConfiguration = this.orderTypeConfigurations.find(
      (orderTypeConfiguration) =>
        orderTypeConfiguration.orderType == order.orderType
    );
    this.historyDetails = getHistoryDetailSummary(
      order,
      orderTypeConfiguration
    );
    for (const x of this.orders) {
      if (x === order) {
        x.showDetails = !x.showDetails;
      } else {
        x.showDetails = false;
      }
    }
  }

  get canAddOrder() {
    return (
      this.orderTypeConfigurations.length > 0 && this.locOrders && this.vfOrders
    );
  }

  /**
   * TRD §4.2 / TDR-14 defense-in-depth: the Non-Covered Items Addendum button should be hidden
   * when the patient has zero orders with `hospicePays === false`. Matches the wave-11 POST
   * payload predicate (PR #1065, commit aacacef101) so the template guard and the click handler
   * agree on what "non-covered" means.
   */
  get hasNonCoveredOrders(): boolean {
    return this.orders?.some((o) => o.hospicePays === false) ?? false;
  }

  presentAddOrderModal(convertToOrder?: boolean) {
    if (!this.userDisplayName) {
      let toast = this.toastCtrl.create({
        message: `Unable to retrieve user info from Resources, please confirm user has been entered.`,
        duration: 3000,
        position: "below",
        cssClass: "error",
      });
      toast.present();
      return;
    }
    let orderPrefilledData: PrefilledData = null;
    if (convertToOrder) {
      orderPrefilledData = {
        patientChartId: this.prefilledData.patientChartId,
        orderType: this.prefilledData.orderType,
        nameDescription: this.prefilledData.nameDescription,
        route: this.prefilledData.route,
        dosage: this.prefilledData.dosage,
        frequency: this.prefilledData.frequency,
        strength: this.prefilledData.strength,
        symptoms: this.prefilledData.symptoms,
        isFreeText: this.prefilledData.isFreeText,
      };
    }
    let addOrderModal = this.modalCtrl.create(
      AddOrder,
      {
        orderTypeConfigurations: this.orderTypeConfigurations,
        patientId: this.navBarService.externalId,
        patientChartId: this.navBarService.getPatientChartId(),
        orders: this.orders,
        formType: convertToOrder ? "convertToOrder" : "entry",
        credentialedResource: this.credentialedResource,
        locOrders: this.locOrders,
        vfOrders: this.vfOrders,
        isEPrescribeEnabled: this.isEPrescribeEnabled(),
        prefilledData: orderPrefilledData,
        benefitPeriodCertificationEndDate:
          this.benefitPeriodCertificationEndDate,
      },
      { enableBackdropDismiss: false, cssClass: "add-order-modal" }
    );

    addOrderModal.onDidDismiss((data) => {
      if (data && data.hasOwnProperty("showEprescribeIframe")) {
        this.showEPrescribeIframe = data.showEprescribeIframe;
        this.destinationPage = "drug-list";
      } else if (data.redirectMedication) {
        const intakeMedsUrl = localStorage.getItem("intakeMedsUrl");
        window.location.href = intakeMedsUrl;
      } else if (data && this.isPartialOrder(data.orderCreateRequest)) {
        this.createOrder(data.orderCreateRequest, data.orderFormState);
      }
    });
    addOrderModal.present();
  }

  isPartialOrder(data: any): data is Partial<Order> {
    return typeof data === "object" && data.hasOwnProperty("orderType");
  }

  isEPrescribeEnabled() {
    return !!(
      this.navBarService.patientEPrescribeFeatureToggleOn() &&
      this.scriptSurePatient
    );
  }

  async createOrder(
    orderCreateRequest: Partial<Order>,
    orderFormState: OrderFormState
  ): Promise<void> {
    // Defaults
    orderCreateRequest.orderingProviderDisplayName =
      orderCreateRequest.orderingProviderDisplayName
        ? orderCreateRequest.orderingProviderDisplayName
        : "N/A";
    orderCreateRequest.approvalType = orderCreateRequest.approvalType
      ? orderCreateRequest.approvalType
      : Order.ApprovalTypeEnum.NoSignatureRequired;

    if (!orderCreateRequest.startDate) {
      orderCreateRequest.startDate = moment(new Date())
        .startOf("day")
        .utc()
        .toISOString();
    }

    if (orderCreateRequest.discontinueDate) {
      orderCreateRequest.discontinueDate = moment(
        orderCreateRequest.discontinueDate
      )
        .utc()
        .toISOString();
    }

    // Add info to postActions
    if (orderCreateRequest.postActions) {
      orderCreateRequest.postActions = orderCreateRequest.postActions.map(
        (postAction) => {
          postAction.displayName = this.userDisplayName;
          postAction.timezone = moment.tz.guess();
          return postAction;
        }
      );
    }

    // Patient info
    orderCreateRequest.externalPatientChartId = this.navBarService.externalId;
    orderCreateRequest.patientName = new LastFirstPatientName().transform({
      firstName: this.patientChart.patient.firstName,
      lastName: this.patientChart.patient.lastName,
      prefix: null,
      middleInitial: this.patientChart.patient.middleInitial,
      suffix: this.patientChart.patient.suffix
        ? this.patientChart.patient.suffix.description
        : null,
    });
    if (!orderCreateRequest.patientName) {
      let toast = this.toastCtrl.create({
        message: `Error retrieving patient info.`,
        duration: 3000,
        position: "below",
        cssClass: "error",
      });
      toast.present();
      return;
    }

    orderCreateRequest.teamId = this.teamId;
    orderCreateRequest.teamName = this.teamName;
    orderCreateRequest.teamQbosId = this.teamQbosId;

    // Created Action
    orderCreateRequest.createdAction = {
      actionType: Action.ActionTypeEnum.Created,
      displayName: this.userDisplayName,
      timezone: moment.tz.guess(),
    };

    if (Order.isOrder(orderCreateRequest)) {
      this.increaseSpinnerCount("Creating order ...");

      // Check if there are orders that need to be discontinued prior to creation
      const discontinueResults = this.discontinueOrdersIfNeeded(
        orderCreateRequest,
        orderFormState
      );
      if (discontinueResults) {
        try {
          await Promise.all(discontinueResults);
        } catch (err) {
          OrderErrorResponse.handleError(
            this.toastCtrl,
            "Error discontinuing order(s)."
          )(err);
          this.decreaseSpinnerCount();
          return;
        }
      }

      // automatically e-sign
      if (
        isProfessionalProvider(this.credentialedResource) &&
        this.credentialedResource.getProviderId() ===
          orderCreateRequest.orderingProviderId
      ) {
        const signature: OrderSignatureRequestSignature = {
          actionType: Action.ActionTypeEnum.ESigned,
          displayName: this.credentialedResource.findDisplayName(),
          timezone: moment.tz.guess(),
          eSignatureDate: new Date().toISOString(),
          attestation: true,
          notes: "Automatically e-Signed",
        };
        if (
          orderCreateRequest.signatureRequests &&
          orderCreateRequest.signatureRequests.created
        ) {
          orderCreateRequest.signatureRequests.created.signature = signature;
        } else {
          orderCreateRequest.signatureRequests = {
            created: {
              signature: signature,
            },
          };
        }
      }

      this.orderManagementDataService
        .createOrderByPatientChartId(this.patientChart.id, orderCreateRequest)
        .finally(() => this.decreaseSpinnerCount())
        .subscribe(() => {
          this.loadData();
          if (orderCreateRequest.orderType === "LEVEL_OF_CARE") {
            this.loadLOCOrders();
          } else if (orderCreateRequest.orderType == "VISIT_FREQUENCY") {
            this.loadVFOrders();
          }
        }, OrderErrorResponse.handleError(this.toastCtrl, "Error creating order."));
    } else {
      const missingFields =
        Order.getMissingFields(orderCreateRequest).join(", ");
      let toast = this.toastCtrl.create({
        message: `Required fields are missing: ${missingFields}`,
        duration: 3000,
        position: "below",
        cssClass: "error",
      });
      toast.present();
    }
  }

  // Checks conditions upon which other orders may be discontinued, and adds them to
  // `discontinuedRequests`.
  // Afterwards, requests to discontinue are made.
  discontinueOrdersIfNeeded(
    orderCreateRequest: Order,
    orderFormState: OrderFormState
  ): Promise<OrderResponse>[] | undefined {
    let discontinuedRequests: {
      orderId: string;
      discontinuedAction: DiscontinuedAction;
    }[] = [];
    const currentLOCOrder = OrderResponse.getCurrentLOC(this.locOrders);
    if (
      orderCreateRequest.orderType == "LEVEL_OF_CARE" &&
      currentLOCOrder &&
      !currentLOCOrder.discontinuedAction
    ) {
      const discontinueDate = moment(orderCreateRequest.startDate)
        .subtract(1, "ms")
        .toISOString();
      discontinuedRequests = [
        {
          orderId: currentLOCOrder.orderId,
          discontinuedAction: this.getDiscontinuedAction(
            orderCreateRequest,
            "Level of Care Change.",
            discontinueDate
          ),
        },
      ];
    } else if (
      orderCreateRequest.orderType == "VISIT_FREQUENCY" &&
      orderFormState.overlappingVfOrders &&
      orderFormState.overlappingVfOrders.length > 0
    ) {
      // The date selected is the later of the overlapping order start date and the
      // start date of the order to be created. This ensures there is no date range
      // overlap.
      discontinuedRequests = orderFormState.overlappingVfOrders.map((order) => {
        const momentBeforeNewStartDate = moment(orderCreateRequest.startDate)
          .subtract(1, "ms")
          .toISOString();
        return {
          orderId: order.orderId,
          discontinuedAction: this.getDiscontinuedAction(
            order,
            "The order has been discontinued because a new order for this discipline has been created.",
            [order.startDate, momentBeforeNewStartDate].sort()[1],
            // CO-3363: optional-chained. Every other read of signatureRequests on
            // this page guards it; an overlapping visit-frequency order that
            // carries none threw here, aborting order creation after the modal had
            // already closed and the user believed the order was saved.
            !!order.signatureRequests?.discontinued
          ),
        };
      });
    }
    if (discontinuedRequests.length > 0) {
      return discontinuedRequests.map((discontinuedRequest) =>
        this.orderManagementDataService
          .updateOrder(
            discontinuedRequest.orderId,
            discontinuedRequest.discontinuedAction
          )
          .toPromise()
      );
    }
  }

  getDiscontinuedAction(
    orderCreateRequest: Order,
    reason: string,
    discontinueDate: string,
    isOverrideDiscontinue: boolean = false
  ): OrderPatchRequest {
    const discontinuedAction: DiscontinuedAction = {
      actionType: Action.ActionTypeEnum.Discontinued,
      reason: reason,
      orderingProviderDisplayName:
        orderCreateRequest.orderingProviderDisplayName,
      orderingProviderId: orderCreateRequest.orderingProviderId,
      displayName: this.userDisplayName,
      timezone: moment.tz.guess(),
      approvalType: orderCreateRequest.approvalType,
      discontinueDate: discontinueDate,
      eSign: isProfessionalProvider(this.credentialedResource),
    };
    if (isOverrideDiscontinue) {
      (discontinuedAction as any).isOverrideDiscontinue = true;
    }
    return discontinuedAction;
  }

  presentErrorToast(err: HttpErrorResponse, message: string) {
    message += " ";
    if (err.error instanceof Error) {
      message += "Client error.";
    } else {
      message += " ";
      if (err.status == 401) {
        message += "User not authorized.";
      } else if (err.status >= 500) {
        // Do not modify message
      } else {
        message += "Communication error.";
      }
    }

    let toast = this.toastCtrl.create({
      message: message,
      duration: 6000,
      position: "below",
      cssClass: "error",
    });

    toast.present();
  }

  public setOrderTypeList(
    orderTypeConfigurations: Array<OrderTypeConfiguration>
  ): void {
    this.orderTypeDropdownItems = orderTypeConfigurations
      .filter((orderTypeConfiguration) => {
        const validation =
          orderTypeConfiguration.orderType &&
          orderTypeConfiguration.orderTypeDisplayName &&
          (orderTypeConfiguration.isDisabled == false ||
            orderTypeConfiguration.isDisabled === undefined);

        const patientCareType = this.patientCareType
          ? this.patientCareType.toUpperCase()
          : "";

        const careTypeFilter =
          !orderTypeConfiguration.careTypes ||
          orderTypeConfiguration.careTypes.includes(patientCareType);

        return validation && careTypeFilter;
      })
      .sort(
        (config1, config2) =>
          (config1.orderTypeSort ? config1.orderTypeSort : Infinity) -
          (config2.orderTypeSort ? config2.orderTypeSort : Infinity)
      )
      .map((orderTypeConfiguration) => {
        return {
          label: orderTypeConfiguration.orderTypeDisplayName,
          value: orderTypeConfiguration.orderType,
        };
      });
  }

  onOrderTypeChange(event: DropdownItem) {
    if (event) {
      this.orderTypes = event.value;
    } else {
      this.orderTypes = null;
    }
  }

  clearFilters() {
    // CO-3363: cleared to null, not undefined. getDateRange() tests `=== null` to
    // decide whether to omit the range entirely, so clearing to undefined made it
    // fall through and emit an empty `dateRange: {}` on every subsequent search
    // instead of omitting the key.
    this.fromDate = null;
    this.toDate = null;
    this.orderTypes = undefined;
    // CO-3363: optional-chained to match its hospiceCoverageDropdown sibling. The
    // order-type ng-select lives behind the eRx-iframe branch of the template, so
    // the ViewChild is undefined whenever the iframe is showing and Clear Filters
    // threw before it ever reached loadData().
    this.orderTypeDropdown?.clearModel();
    this.hospiceCoverage = null;
    this.hospiceCoverageDropdown?.clearModel();
    this.searchForm.get("fromDate").setValue("");
    this.searchForm.get("toDate").setValue("");
    this.loadData();
  }

  applyFiltersOS() {
    this.currentPage = 0;
    this.loadData();
  }

  createSearchRequestBody() {
    //TODO add signed orders filter
    let details: OrderSearchRequest = {};
    details.patientChartId =
      this.externalIdProviderPanel > 0
        ? this.externalIdProviderPanel
        : this.patientChart.id;
    details.orderType = this.orderTypes;
    details.queryString = this.search === "" ? undefined : this.search;
    details.dateRange = this.getDateRange();
    details.from = this.currentPage * this.pageSize;
    details.size = this.pageSize;
    details.sortBy = {
      name: this.sortColumn,
      value: this.sortDirection,
    };
    details.includeDiscontinued = this.discontinuedOrdersEnabled;
    details.signedStatus = this.signedOrdersEnabled
      ? this.signedStatus
      : OrderSignatureRequest.SignedStatusEnum.No;
    details.hospicePays = this.hospiceCoverage;
    this.orderSearchDetails = details;
  }

  /**
   * CO-3363: rewritten so the range is decided BEFORE it is built, not patched
   * afterwards.
   *
   * The original set `field` for any non-null value and then deleted the bound
   * again if it did not parse. A date input holding an empty string ("" — which
   * is what the reactive form seeds and what clearing a picker leaves behind) is
   * neither null nor a valid date, so both bounds were deleted while `field`
   * survived: every search then carried `dateRange: {field: "startDate"}`, a
   * filter with no bounds at all. It also tested `=== null`, so a filter cleared
   * to `undefined` produced an empty `{}` instead of omitting the key.
   *
   * A bound now has to be genuinely usable to appear, and with no usable bound
   * the whole key is omitted.
   */
  getDateRange(): OrderSearchDateRange | undefined {
    const usable = (value: any) =>
      value !== null &&
      value !== undefined &&
      value !== "" &&
      moment(value).isValid();

    const fromDate = usable(this.fromDate)
      ? this.formatDate(this.fromDate)
      : undefined;
    const toDate = usable(this.toDate) ? this.formatDate(this.toDate) : undefined;

    if (!fromDate && !toDate) {
      return undefined;
    }

    const dateRange: OrderSearchDateRange = { field: DATE_RANGE_COLUMN };
    if (fromDate) {
      dateRange.fromDate = fromDate;
    }
    if (toDate) {
      dateRange.toDate = toDate;
    }
    return dateRange;
  }

  formatDate(date) {
    if (date !== null) {
      return moment(date).format("YYYY-MM-DD");
    } else {
      return date;
    }
  }

  presentEditViewPopover(
    $event,
    order: OrderResponse,
    signatureRequest: Action.SignatureRequestEnum
  ) {
    let discontinueOrder: boolean = false;
    if (signatureRequest === Action.SignatureRequestEnum.Discontinued) {
      discontinueOrder = true;
    }
    if (!this.userDisplayName) {
      let toast = this.toastCtrl.create({
        message: `Unable to retrieve user info from Resources, please confirm user has been entered.`,
        duration: 3000,
        position: "below",
        cssClass: "error",
      });
      toast.present();
      return;
    }
    let popover = this.popoverCtrl.create(
      EditViewPopoverPage,
      {
        hideOrderManagment: true,
        isPatientChart: true,
        showButtonsAsList: false,
        showMedicationEdit: false,
        onlyDelete: true,
        disabledIcon: true,
        deleteLabel: "Remove",
        order: order,
        orderTypeConfigurations: this.orderTypeConfigurations,
        orders: this.orders,
        userDisplayName: this.userDisplayName,
        orderType: order.orderType,
        orderPatientChartId: this.patientChart.id,
        shouldDisableCancel:
          !!order.discontinuedAction?.discontinueDate ||
          !!order.canceledAction?.actionType,
        locOrders: this.locOrders,
        vfOrders: this.vfOrders,
        credentialedResource: this.credentialedResource,
        signatureRequest: signatureRequest,
        includeAmendHospiceCoverage:
          this.orderTypesWithHospiceCoverage.includes(order.orderType),
        ePrescribeEnabled: this.isEPrescribeEnabled(),
        patientStatus: this.referralsService.PATIENT_STATUS,
        patientDischargedDateTime: this.patientChart.dischargedDatetime,
      },
      {
        cssClass: "more-options-list-popover",
      }
    );
    popover.present({
      ev: $event,
    });

    // add mode = which is the object returned from edit-view-popover?
    popover.onDidDismiss((mode) => {
      if (mode) {
        if (this.addOrderModalShouldBeOpenedAfterLastLOCCanceled(mode)) {
          this.refresh();
          this.presentNewLOCOrderModal();
        } else if (mode.discontinue) {
          this.refresh();
        } else if (mode.updateFile$) {
          this.increaseSpinnerCount("Adding files to order...");
          (mode.updateFile$ as Observable<OrderResponse>)
            .finally(() => this.decreaseSpinnerCount())
            .subscribe((_data) => {
              this.loadData();
            }, OrderErrorResponse.handleError(this.toastCtrl, "Error adding files to order."));
        } else if (mode.updateOrder$) {
          this.increaseSpinnerCount("Signing order ...");
          (mode.updateOrder$ as Observable<OrderResponse>)
            .finally(() => this.decreaseSpinnerCount())
            .subscribe((_data) => {
              this.loadData();
            }, OrderErrorResponse.handleError(this.toastCtrl, "Error signing order."));
        } else if (mode.amendedOrder$) {
          this.increaseSpinnerCount("Amending Order ...");
          (mode.amendedOrder$ as Observable<OrderResponse>)
            .finally(() => this.decreaseSpinnerCount())
            .subscribe((_data) => {
              this.loadData();
            }, OrderErrorResponse.handleError(this.toastCtrl, "Error amending order."));
        } else if (mode.showEprescribeIframe) {
          this.showEPrescribeIframe = mode.showEprescribeIframe;
          this.destinationPage = "patient";
        }
      }
    });
  }

  addOrderModalShouldBeOpenedAfterLastLOCCanceled(mode: any): boolean {
    return (
      this.modeDiscontinueKeyExistsAndIsTrue(mode) &&
      this.modeOpenAddOrderModalKeyExistsAndIsTrue(mode)
    );
  }

  modeDiscontinueKeyExistsAndIsTrue(mode: any): boolean {
    return mode.discontinue && mode.discontinue === true;
  }

  modeOpenAddOrderModalKeyExistsAndIsTrue(mode: any): boolean {
    return mode.openAddOrderModal && mode.openAddOrderModal === true;
  }

  presentNewLOCOrderModal() {
    // Prep The Data For The AddOrder Modal
    this.setPrefilledData();
    this.setModalParams();
    this.setModalOpts();

    let addOrderModal = this.modalCtrl.create(
      AddOrder,
      this.addOrderModalParams,
      this.addOrderModalOpts
    );

    addOrderModal.present();

    addOrderModal.onDidDismiss((data) => {
      if (data && this.isPartialOrder(data.orderCreateRequest)) {
        this.createOrder(data.orderCreateRequest, data.orderFormState);
      }
    });
  }

  setPrefilledData() {
    this.prefilledData = {
      patientChartId: this.navBarService.getPatientChartId(),
      orderType: "LEVEL_OF_CARE",
    };
  }

  setModalParams() {
    this.addOrderModalParams = {
      orderTypeConfigurations: this.orderTypeConfigurations,
      patientId: this.navBarService.externalId,
      patientChartId: this.navBarService.getPatientChartId(),
      orders: this.orders,
      formType:
        "openLevelOfCareAddOrderModalAfterCancellingPreviousLevelOfCare",
      credentialedResource: this.credentialedResource,
      locOrders: this.locOrders,
      vfOrders: this.vfOrders,
      isEPrescribeEnabled: this.isEPrescribeEnabled(),
      prefilledData: this.prefilledData,
    };
  }

  setModalOpts(): void {
    this.addOrderModalOpts = {
      enableBackdropDismiss: false,
      cssClass: "add-order-modal",
    };
  }

  showDocuments(
    $event,
    order: OrderResponse,
    signatureRequest: Action.SignatureRequestEnum
  ) {
    const orderSignatureRequest =
      signatureRequest === "created"
        ? order.signatureRequests.created
        : order.signatureRequests.discontinued;

    this.increaseSpinnerCount("Loading File(s)...");
    of(...orderSignatureRequest.documentIds)
      .pipe(
        mergeMap((id) =>
          this.documentSvc.getDocumentToView(id).pipe(
            catchError((err) => {
              this.presentErrorToast(
                err,
                `Document failed to load: ${err.message}`
              );
              return of(undefined);
            })
          )
        ),
        filter<ArrayBuffer | undefined>((document) => document !== undefined)
      )
      .toArray()
      .subscribe((documents) => {
        documents.forEach((data) => {
          const file = new Blob([data], { type: "application/pdf" });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL, "_blank");
        });
        this.decreaseSpinnerCount();
      });
  }

  increaseSpinnerCount(message: string) {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }
  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0 && this.spinner) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  getOrderTypeDisplayName(orderTypeSubtype: OrderTypeSubtype) {
    return getOrderTypeDisplayName(
      orderTypeSubtype,
      this.orderTypeConfigurations
    );
  }

  getDiscontinuedProviderDisplayName(order: StatefulOrderResponse) {
    return getDiscontinuedProviderDisplayName(order);
  }

  formatPreviousValueDisplay(value: string, changeMadeTo: string) {
    const allowedKeys = ["address1", "address2", "city", "state", "zip", "ZIP"];
    if (changeMadeTo === "Patient Location") {
      var data = JSON.parse(value);
      var formattedData = allowedKeys
        .filter((key) => data[key] !== null && data[key] !== undefined)
        .map((key) => data[key])
        .filter((value) => value !== "-")
        .join(", ");
      return formattedData;
    }
    return value;
  }

  onToggleDiscontinuedOrders(toggle: Toggle) {
    this.discontinuedOrdersEnabled = toggle.value;
    this.loadData();
  }

  sortBy(selectedColumn: string) {
    if (this.sortColumn != selectedColumn) {
      this.sortColumn = selectedColumn;
      this.sortDirection = "desc";
    } else {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    }
    this.loadData();
  }

  getCaretState(column: string) {
    if (this.sortColumn === column && this.sortDirection === "asc") {
      return "ios-arrow-up";
    }
    return "ios-arrow-down";
  }

  /**
   * CO-3363: exposes sort state as the standard `aria-sort` token so assistive
   * technology and e2e can read which column is sorted and in which direction,
   * rather than inferring it from an ion-icon's `name` attribute.
   */
  getAriaSort(column: string): "none" | "ascending" | "descending" {
    if (this.sortColumn !== column) {
      return "none";
    }
    return this.sortDirection === "asc" ? "ascending" : "descending";
  }

  getItemsBySearchFilter() {
    this.currentPage = 0;
    this.loadData();
  }

  clearSearchBar() {
    this.search = null;
    this.currentPage = 0;
    this.loadData();
  }
  openOptionsPopover(ev) {
    let options = [];
    options = this.selectPrintOptions.map((a) => ({ ...a }));
    const popover = this.popoverCtrl.create(
      "OMPrintMenuPage",
      {
        options,
      },
      { cssClass: "multiple-select-custom-popover" }
    );
    popover.onDidDismiss((option) => {
      if (option && option.key == "print") {
        this.downloadOrderReport(false);
      } else if (option && option.key == "fax") {
        this.handleFax(false);
      }
    });
    popover.present({ ev });
  }

  downloadOrderReport(printAll: boolean, reportName?: string) {
    let name = reportName ? reportName : "order_management_report";

    const orderIds = this.checkedOrders;

    if (orderIds.length == 0) {
      this.toastError(NO_ORDERS_TO_PRINT, false);
      return;
    }

    const values = orderIds.toString();

    this.reportExecutionService.downloadReport(
      name,
      this.buildReportPayload(printAll, values),
      this.reportExecutionService.buildReportFilename(name, "pdf")
    );

    const trackOrders = Utils.createTrackOrdersPayload(
      orderIds.map((v) => ({ orderId: v, medicationId: "" })),
      Action.ActionTypeEnum.Printed,
      moment.tz.guess(),
      this.userDisplayName
    );

    if (trackOrders.length > 0) {
      // 204 response, handled error not required
      this.orderManagementDataService.bulkUpdateOrders(trackOrders).subscribe(
        () => {},
        () => {}
      );
    }
  }

  makeSelectPrintOptions() {
    // Start with print options
    let options = [...this.printOptions];

    // Add fax options only if WestFax or WorldViewFax toggle is enabled
    // Using getValue() to get the current value from BehaviorSubject
    const isWestFaxEnabled = this.stateData.isWestFaxToggleOn.getValue();
    const isWorldViewFaxEnabled =
      this.stateData.isWorldViewFaxToggleOn.getValue();
    if (isWestFaxEnabled || isWorldViewFaxEnabled) {
      options = [...options, ...this.faxOptions];
    }

    this.selectPrintOptions = options.map((e) => {
      return { name: e.displayName, value: e, checked: true };
    });
  }

  buildReportPayload(printAll: boolean, listOrderId: string) {
    // CO-3363: see getUserInformation — getUser() is null on a cold deep link, and
    // `.tenantId` threw here from inside the print handler.
    const user = this.authSvc.getUser();
    const orgId = user ? user.tenantId : "";
    let payload = {
      pOrgID: orgId.toString(),
      pPatientChartID: this.navBarService.externalId.toString(),
      pOrderID: printAll ? "All" : listOrderId,
      pOrderStatus: "All", // Right now we only have print all and print filtered options, both need pOrderStatus 'All'
    };
    return payload;
  }

  trimString(string, length) {
    return string.length > length
      ? string.substring(0, length) + "..."
      : string;
  }

  fromDateHandler() {
    let fromDate = this.formatDate(this.fromDate);
    let toDate = this.formatDate(this.toDate);
    if (moment(toDate).isBefore(fromDate)) {
      this.fromDate = null;
      this.searchForm.get("fromDate").setValue("");
      this.toastError("From Date must be less than To Date.");
    }
  }

  toDateHandler() {
    let fromDate = this.formatDate(this.fromDate);
    let toDate = this.formatDate(this.toDate);
    if (moment(fromDate).isAfter(toDate)) {
      this.toDate = null;
      this.searchForm.get("toDate").setValue("");
      this.toastError("To Date must be greater than From Date.");
    }
  }

  selectAllOrders(event: Checkbox) {
    for (const order of this.orders) {
      this.checkedOrdersMap[order.orderId] = event.checked;
      this.onCheckOrder({ value: event.checked }, order.orderId);
    }
  }

  onCheckOrder(event: { value: boolean }, orderId: string) {
    if (event.value) {
      // CO-3363: this pushed unconditionally, so checking a row individually and
      // then using Select All recorded the same orderId twice. The duplicates
      // reached the print payload (`orderIds.toString()`) and the Printed audit
      // entries built by Utils.createTrackOrdersPayload, double-counting the order.
      if (this.checkedOrders.indexOf(orderId) === -1) {
        this.checkedOrders = [...this.checkedOrders, orderId];
      }
    } else {
      this.checkedOrders = this.checkedOrders.filter(
        (checkedOrderId) => checkedOrderId !== orderId
      );
    }
  }

  toastError(message: string, showCloseButton = false) {
    let toast = this.toastCtrl.create({
      message: message,
      showCloseButton: showCloseButton,
      duration: showCloseButton ? undefined : 6000,
      position: "below",
      cssClass: "error",
    });
    toast.present().then();
  }

  onChangeDatepicker(event: any, control: string) {
    if (event) {
      let value = event.target.value;
      let valueFormatted = moment(value, "MM/DD/YYYY", true);
      if (valueFormatted.isValid()) {
        switch (control) {
          case "fromDate":
            this.fromDate = new Date(valueFormatted);
            this.searchForm.get("fromDate").setValue(this.fromDate);
            this.fromDateHandler();
            break;
          case "toDate":
            this.toDate = new Date(valueFormatted);
            this.searchForm.get("toDate").setValue(this.toDate);
            this.toDateHandler();
            break;
        }
        this.error = false;
      } else {
        this.error = true;
      }
    }
  }

  onToggleSignedOrders(toggle: Toggle) {
    this.signedOrdersEnabled = toggle.value;
    this.loadData();
  }

  existingOrderSearch(orderType: string, includeDiscontinued: boolean) {
    return this.orderManagementDataService.searchOrders({
      patientChartId:
        this.externalIdProviderPanel > 0
          ? this.externalIdProviderPanel
          : this.patientChart.id,
      orderType: orderType,
      from: 0,
      size: 100,
      sortBy: {
        name: "startDate",
        value: "desc",
      },
      includeDiscontinued: includeDiscontinued,
    });
  }

  loadLOCOrders() {
    this.locOrders = null;
    this.existingOrderSearch("LEVEL_OF_CARE", true).subscribe((orders) => {
      this.locOrders = orders.hits ? orders.hits : [];
    });
  }

  loadVFOrders() {
    this.vfOrders = null;
    this.existingOrderSearch("VISIT_FREQUENCY", false).subscribe((orders) => {
      this.vfOrders = orders.hits ? orders.hits : [];
    });
  }

  loadCurrentbenefitPeriodCertificationEndDate() {
    const currentDateForBenefitPeriod = moment()
      .local(true)
      .format("YYYY-MM-DD");
    this.certificationService
      .GetCurrentBenefitPeriod(
        this.patientChart.externalId,
        currentDateForBenefitPeriod
      )
      .pipe()
      .take(1)
      .subscribe(
        (benefitPeriod: any) => {
          if (benefitPeriod) {
            this.benefitPeriodCertificationEndDate = benefitPeriod.endDate
              ? moment(benefitPeriod.endDate).toDate()
              : undefined;
          }
        },
        (error) => {
          console.error(error);
        }
      );
  }

  orderIsDeclined(order: OrderResponse) {
    return Order.isVisitFrequencyDeclined(order);
  }

  setIsOrderRejected(order) {
    return (
      order.signatureRequests?.created?.signedStatus ===
      OrderSignatureRequest.SignedStatusEnum.Rejected
    );
  }

  setIsOrderDiscontinued(order) {
    return order.discontinuedAction != null;
  }

  handleExternalExport() {
    // TODO: Implement External Export functionality
    let toast = this.toastCtrl.create({
      message: "External Export functionality coming soon",
      duration: 3000,
      position: "below",
      cssClass: "info",
    });
    toast.present();
  }

  buildFaxPayload(faxAll: boolean, listOrderId: string) {
    // CO-3363: see getUserInformation. A null getUser() threw here before
    // handleFax's own "Could not send Fax, please contact your administrator"
    // guard could report the missing sender phone.
    const user = this.authSvc.getUser();
    let payload = {
      patientChartId: this.navBarService.externalId.toString(),
      orderId: faxAll ? "All" : listOrderId,
      companyName: user ? user.tenantName : "",
      senderPhone: user ? user.tenantPhone : "",
      orderStatus: "All",
    };
    return payload;
  }

  handleFax(faxAll: boolean = false) {
    // Check if we have orders to fax before opening modal
    const orderIds = this.checkedOrders;

    if (orderIds.length == 0) {
      this.toastError(NO_ORDERS_TO_FAX, false);
      return;
    }

    // Create callback function to handle API call
    const sendFaxCallback = (faxData: any): Promise<boolean> => {
      return new Promise((resolve) => {
        // Remove formState from the request body
        const { formState, ...cleanFaxData } = faxData;

        let requestBody = this.buildFaxPayload(faxAll, orderIds.toString());
        requestBody = { ...requestBody, ...cleanFaxData };

        if (!requestBody.senderPhone || requestBody.senderPhone === "") {
          this.toastError(
            "Could not send Fax, please contact your administrator",
            false
          );
          resolve(false); // Don't close modal if validation fails
          return;
        }

        // Show loading spinner while sending fax
        this.increaseSpinnerCount("Sending fax...");

        this.orderManagementDataService.faxOrders(requestBody).subscribe(
          () => {
            this.decreaseSpinnerCount();
            // Handle fax sent successfully
            let toast = this.toastCtrl.create({
              message: "Fax sent successfully",
              duration: 3000,
              position: "bottom",
              cssClass: "success",
            });
            toast.present();
            resolve(true); // Close modal on success
          },
          () => {
            this.decreaseSpinnerCount();
            this.toastError("Error sending fax, please try again", false);
            resolve(false); // Don't close modal on error
          }
        );
      });
    };

    const faxModal = this.modalCtrl.create(
      FaxModalComponent,
      {
        sendFaxCallback: sendFaxCallback,
        patChartId: this.patientChart?.id,
      },
      {
        cssClass: "popup-medium",
        enableBackdropDismiss: false,
      }
    );

    faxModal.present();
  }

  b64toBlob(b64Data, contentType = "", sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: contentType });
  }
}



// WEBPACK FOOTER //
// ./src/pages/orders-entry-om/orders-entry-om/order-entry-om.ts